import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { ExpenseDto } from "./transactions";

export interface StudentDto {
  id: string;
  name: string;
  active: boolean;
}

export interface ScholarshipAwardListItem {
  id: string;
  studentId: string;
  studentName: string;
  institution: string;
  fundId: string;
  fundName: string;
  allocatedAmount: string;
  committedAmount: string;
  paidAmount: string;
  remainingAmount: string;
  period: string;
  createdAt: string;
}

export interface ScholarshipAwardDetail extends ScholarshipAwardListItem {
  allocations: { id: string; fundId: string; amount: string }[];
  paymentRequests: {
    id: string;
    requestedAmount: string;
    createdAt: string;
    expense: ExpenseDto;
  }[];
}

export interface ScholarshipFundOption {
  id: string;
  name: string;
  donorId: string | null;
}

export function useScholarshipFundsQuery(enabled = true) {
  return useQuery({
    queryKey: ["scholarship-funds"],
    queryFn: () => api.get<{ funds: ScholarshipFundOption[] }>("/scholarship-funds"),
    enabled,
  });
}

export function useStudentsQuery(enabled = true) {
  return useQuery({
    queryKey: ["students"],
    queryFn: () => api.get<{ students: StudentDto[] }>("/students"),
    enabled,
  });
}

export function useScholarshipAwardsQuery(enabled = true) {
  return useQuery({
    queryKey: ["scholarship-awards"],
    queryFn: () => api.get<{ awards: ScholarshipAwardListItem[] }>("/scholarship-awards"),
    enabled,
  });
}

export function useScholarshipAwardQuery(awardId: string | null) {
  return useQuery({
    queryKey: ["scholarship-awards", awardId],
    queryFn: () => api.get<{ award: ScholarshipAwardDetail }>(`/scholarship-awards/${awardId}`),
    enabled: Boolean(awardId),
  });
}

export function useCreateStudentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) => api.post<{ student: StudentDto }>("/students", body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });
}

export function useCreateScholarshipAwardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      studentId: string;
      institution: string;
      fundId: string;
      allocatedAmount: string;
      period: string;
      allocations: { fundId: string; amount: string }[];
    }) => api.post<{ award: ScholarshipAwardDetail }>("/scholarship-awards", body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["scholarship-awards"] });
    },
  });
}

export function useCreatePaymentRequestMutation(awardId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      amount: string;
      expenseDate: string;
      payee?: string;
      description?: string;
      missingEvidenceReason?: string;
      submit?: boolean;
    }) =>
      api.post<{ paymentRequest: { id: string }; award: ScholarshipAwardDetail }>(
        `/scholarship-awards/${awardId}/payment-requests`,
        body,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["scholarship-awards"] });
      await queryClient.invalidateQueries({ queryKey: ["scholarship-awards", awardId] });
      await queryClient.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}
